import { enforceFormat } from './PhoneNumberFormatting';
import { IErrorMessage } from '../../components/errorMessage/errorMessage.type';
import { TranslationHelpers } from '../../../../i18n/translationHelpers';
import { parsePhoneNumberFromString, PhoneNumber } from 'libphonenumber-js';
import { PhoneNumberUtil } from 'google-libphonenumber';

export const MAX_LENGTH = 64;

export function isPhoneNumberModified(
    initialPhoneNumber: string,
    editedPhoneNumber?: string,
): boolean {
    const formattedInitialPhoneNum = enforceFormat(initialPhoneNumber);

    if (editedPhoneNumber) {
        const formattedEditedPhoneNum = enforceFormat(editedPhoneNumber);
        return formattedInitialPhoneNum !== formattedEditedPhoneNum;
    }

    return false;
}

export function validatePhoneNumber(
    value: string,
    initialDisplayValue: string,
    required: boolean,
    currentCallingCode?: string,
): IErrorMessage {
    const numberProvided =
        !(!value || value.trim() === '') &&
        currentCallingCode &&
        value.length > currentCallingCode.length;
    const phoneNumber = getPhoneNumber(value);
    const phoneNumberModified = isPhoneNumberModified(initialDisplayValue, value);
    if (numberProvided) {
        if (!phoneNumberModified) {
            // Do not validate number if it is the same
            // number as what's on RID/ MyR Backend
            return '';
        }
    } else {
        if (required) {
            return TranslationHelpers.getMessageDescriptor('error.phone_number.required');
        } else {
            // Do not validate if no value and not required
            return '';
        }
    }

    if (
        !phoneNumber ||
        (!phoneNumber.isValid() && !isValidJP060Number(phoneNumber)) ||
        value.length > MAX_LENGTH
    ) {
        return TranslationHelpers.getMessageDescriptor('error.phone_number.invalid');
    }

    return '';
}

export function isEmpty(value?: string) {
    return !value || value.trim() === '';
}

export function getCountryCode(
    defaultCountry?: string,
    value?: string,
    phoneNumber?: PhoneNumber,
    currentCountry?: string,
    currentCallingCode?: string,
    isTouched?: boolean,
): string | undefined {
    let countryCode;

    if (isEmpty(value) && !isTouched) {
        // If empty and not touched then use default country code
        countryCode = defaultCountry;
    } else if (isEmpty(value)) {
        // Reset country code to undefined if empty
        countryCode = undefined;
    } else if (phoneNumber && phoneNumber.country) {
        // If phone number recognised and country code detected use it
        countryCode = phoneNumber.country;
    } else if (currentCallingCode && value && value.startsWith(currentCallingCode)) {
        // If country code is not detected but number starts with current calling code keep country code
        countryCode = currentCountry;
    }

    return countryCode;
}

// libphonenumber-js doesn't recognise 060 as a valid JP mobile prefix.
// nationalNumber has the leading 0 stripped, so 11-digit 060-XXXX-XXXX → 10-digit nationalNumber.
export function isValidJP060Number(phoneNumber: PhoneNumber): boolean {
    return phoneNumber.country === 'JP' && /^60\d{8}$/.test(String(phoneNumber.nationalNumber));
}

export function getPhoneNumber(value?: string): PhoneNumber | undefined {
    if (value) {
        value = enforceFormat(value);
    }
    return value ? parsePhoneNumberFromString(value) : undefined;
}

export function getDefaultValue(currentCallingCode?: string): string {
    return currentCallingCode ? currentCallingCode : ' ';
}

export const normalRuleRegex = /^\d{1,8}-\d{1,8}-\d{1,8}$/;
export const specialRuleRegex = /^(015829|016528|016532|016534)-\d{1,8}(-\d{1,8})?$/;

export function validateJPPhoneNumber(value: string, numberType = 'phoneNumber'): boolean {
    if (numberType === 'faxNumber') {
        return normalRuleRegex.test(value) || specialRuleRegex.test(value);
    }
    if (numberType === 'phoneNumber' && specialRuleRegex.test(value)) {
        return true;
    }

    try {
        let stripped = value.replace(/[^0-9]/g, '');
        if (/^60\d{8}$/.test(stripped)) {
            stripped = `0${stripped}`;
        }
        const valueLength = stripped.length;
        if (valueLength !== 10 && valueLength !== 11) {
            return false;
        }
        // 060 is a valid JP mobile prefix (11 digits only) not recognised by google-libphonenumber
        if (/^060/.test(stripped)) {
            return valueLength === 11;
        }
        const phoneUtil = PhoneNumberUtil.getInstance();
        const parsedNumber = phoneUtil.parse(value, 'JP');
        return phoneUtil.isValidNumberForRegion(parsedNumber, 'JP');
    } catch (error) {
        return false;
    }
}
